import { safeCapture } from "@/lib/analytics/safeCapture";
import { reloadOnce } from "@/lib/recovery/reloadOnce";

/**
 * Detection + recovery for a failed dynamic-import chunk fetch.
 *
 * There is no chunk-specific handling anywhere else in the repo: `deploymentId`
 * (next.config.ts) + Vercel Skew Protection cover most deploy-time chunk rotation,
 * but not the first navigation before the `__vdpl` cookie lands, a session outliving
 * the 12h skew window, or a plain transient network failure.
 */

// Cross-browser/cross-bundler message variants for a failed chunk fetch. Deliberately
// does NOT match Safari's generic "Load failed" (TypeError message on any failed
// fetch, chunk or otherwise) — that string is indistinguishable from an ordinary
// clientFetch failure (e.g. the quiz step-submit network error, TRA-345) and matching
// it here would trigger an unwanted hard reload mid-quiz for something unrelated to a
// stale chunk.
const CHUNK_ERROR_MESSAGE_RE =
  /Loading chunk|Failed to load chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === "ChunkLoadError") return true;
  return CHUNK_ERROR_MESSAGE_RE.test(error.message);
}

const RELOAD_MARKER_KEY = "bsa_chunk_reload_at";

// A tab that recovers cleanly from one chunk error shouldn't be permanently unguarded
// for the rest of its life — a long-lived /quiz/results tab is exactly the kind of
// session that can hit two genuinely separate deploy-skew incidents hours apart. The
// marker is a timestamp, not a boolean, so a later incident past the cooldown still
// gets an automatic reload, while the 10-minute floor between reloads still rules out
// a loop.
const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

// Give posthog.captureException (called by the boundary just before this) and the
// chunk_load_failed capture below a moment to flush their batch before navigation —
// a synchronous reload right after queuing an event is a known way to lose it.
const RELOAD_DELAY_MS = 200;

/**
 * Hard-reloads the page (after a short delay) when `error` is a chunk-load failure —
 * `unstable_retry()` in the error boundaries re-renders against the same stale chunk
 * URL and can never recover one; only a fresh navigation re-fetches the build
 * manifest. Guarded by a `sessionStorage` timestamp + cooldown so a permanently-broken
 * chunk (a bad deploy) can't spin the tab in a reload loop — a repeat within the
 * cooldown window falls through to the boundary's normal "Try again" / "Go home" card.
 *
 * If `sessionStorage` can't be read or written at all (private-mode lockdown, a
 * third-party-frame context, quota exhaustion), we can't durably guarantee the
 * cooldown, so we deliberately do NOT reload rather than risk reloading on every
 * mount — a loop is a strictly worse outcome than a user occasionally seeing the
 * boundary's normal card instead of an automatic recovery.
 *
 * Returns `true` if a reload was scheduled (useful for tests; the caller has nothing
 * else to do either way — the reload navigates the page away once it fires).
 */
export function reloadOnceForChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  return reloadOnce({
    markerKey: RELOAD_MARKER_KEY,
    cooldownMs: RELOAD_COOLDOWN_MS,
    delayMs: RELOAD_DELAY_MS,
    onBeforeReload: () =>
      safeCapture("chunk_load_failed", { surface: "error_boundary", recovered: false }),
  });
}
